/**
 * IT Offboarding Form - Public page for IT team to complete offboarding checklist
 * Enhanced with 6-item detailed IT revocation checklist
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  CssBaseline,
  Divider,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useParams } from 'react-router-dom';
import api from '../services/api';

interface OffboardingFormData {
  first_name: string;
  last_name: string;
  middle_name?: string;
  work_email?: string;
  last_working_day?: string;
  exit_reason?: string;
  notes?: string;
}

interface OffboardingChecklistState {
  email_disabled: boolean;
  vpn_revoked: boolean;
  system_accounts_disabled: boolean;
  access_cards_deactivated: boolean;
  equipment_collected: boolean;
  data_backup_completed: boolean;
}

const ITOffboardingForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  // Detect system theme preference
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  // Create theme based on system preference
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: prefersDarkMode ? 'dark' : 'light',
        },
      }),
    [prefersDarkMode]
  );

  const [formData, setFormData] = useState<OffboardingFormData | null>(null);
  const [itNotes, setItNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Checklist state - 6 detailed IT revocation items
  const [checklistState, setChecklistState] = useState<OffboardingChecklistState>({
    email_disabled: false,
    vpn_revoked: false,
    system_accounts_disabled: false,
    access_cards_deactivated: false,
    equipment_collected: false,
    data_backup_completed: false,
  });

  // Individual notes for each checklist item
  const [itemNotes, setItemNotes] = useState({
    email_disabled_notes: '',
    vpn_revoked_notes: '',
    system_accounts_disabled_notes: '',
    access_cards_deactivated_notes: '',
    equipment_collected_notes: '',
    data_backup_completed_notes: '',
  });

  useEffect(() => {
    const loadFormData = async () => {
      if (!token) {
        setError('Invalid link - no token provided');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/offboarding/token/${token}`);
        setFormData(response.data);

        // Initialize checklist state from form data (if already partially filled)
        setChecklistState({
          email_disabled: response.data.email_disabled || false,
          vpn_revoked: response.data.vpn_revoked || false,
          system_accounts_disabled: response.data.system_accounts_disabled || false,
          access_cards_deactivated: response.data.access_cards_deactivated || false,
          equipment_collected: response.data.equipment_collected || false,
          data_backup_completed: response.data.data_backup_completed || false,
        });

        // Initialize item notes (if already partially filled)
        setItemNotes({
          email_disabled_notes: response.data.email_disabled_notes || '',
          vpn_revoked_notes: response.data.vpn_revoked_notes || '',
          system_accounts_disabled_notes: response.data.system_accounts_disabled_notes || '',
          access_cards_deactivated_notes: response.data.access_cards_deactivated_notes || '',
          equipment_collected_notes: response.data.equipment_collected_notes || '',
          data_backup_completed_notes: response.data.data_backup_completed_notes || '',
        });

        setItNotes(response.data.it_notes || '');
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail || 'Failed to load form data. The link may be invalid or expired.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) return;

    // Check if all checklist items are completed
    const allItemsComplete = Object.values(checklistState).every(value => value === true);

    if (!allItemsComplete) {
      setError('Please complete all 6 IT revocation checklist items before submitting.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.put(`/offboarding/token/${token}/submit-it-form`, {
        ...checklistState,
        ...itemNotes,
        it_notes: itNotes,
      });
      setSuccess(true);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to submit IT revocation checklist';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  // Success state
  if (success) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            py: 4,
          }}
        >
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: '#F97316', // Intentional brand color
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48, color: 'primary.contrastText' }} />
            </Box>
            <Card
              sx={{
                width: '100%',
                maxWidth: 480,
                borderRadius: '20px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                backgroundColor: prefersDarkMode ? 'rgba(30, 41, 59, 0.75)' : 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                border: prefersDarkMode ? '1px solid rgba(51, 65, 85, 0.5)' : '1px solid rgba(255, 255, 255, 0.5)',
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: '#F97316', mb: 2 }}
                >
                  IT Offboarding Completed Successfully!
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  All IT offboarding tasks have been successfully marked as complete.
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  The employee's status has been automatically updated to <strong>TERMINATED/RESIGNED</strong>.
                </Typography>
                <Typography color="text.secondary">
                  HR and Admin have been notified of the completion.
                </Typography>
                <Typography variant="body2" sx={{ mt: 3, color: 'text.disabled' }}>
                  You can close this window.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>
      </ThemeProvider>
    );
  }

  // Error state (when form data couldn't be loaded)
  if (!formData) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            py: 4,
          }}
        >
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Card
            sx={{
              width: '100%',
              maxWidth: 480,
              borderRadius: '20px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
              backgroundColor: prefersDarkMode ? 'rgba(30, 41, 59, 0.75)' : 'rgba(255, 255, 255, 0.75)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: prefersDarkMode ? '1px solid rgba(51, 65, 85, 0.5)' : '1px solid rgba(255, 255, 255, 0.5)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Alert severity="error" sx={{ borderRadius: '12px' }}>
                {error || 'Unable to load the form. The link may be invalid or expired.'}
              </Alert>
            </CardContent>
          </Card>
        </Container>
      </Box>
      </ThemeProvider>
    );
  }

  // Calculate completion percentage
  const completedItems = Object.values(checklistState).filter(value => value === true).length;
  const totalItems = Object.keys(checklistState).length;
  const completionPercentage = Math.round((completedItems / totalItems) * 100);

  // Helper to format date
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Not specified';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Checklist items configuration
  const checklistItems = [
    {
      key: 'email_disabled' as const,
      label: '❌ Email Account Disabled',
      description: 'Disable/suspend the employee\'s work email account',
      notesKey: 'email_disabled_notes' as const,
    },
    {
      key: 'vpn_revoked' as const,
      label: '❌ VPN Access Revoked',
      description: 'Remove VPN access and credentials',
      notesKey: 'vpn_revoked_notes' as const,
    },
    {
      key: 'system_accounts_disabled' as const,
      label: '❌ System Accounts Disabled',
      description: 'Disable access to all company systems, databases, and applications',
      notesKey: 'system_accounts_disabled_notes' as const,
    },
    {
      key: 'access_cards_deactivated' as const,
      label: '❌ Access Cards Deactivated',
      description: 'Deactivate physical access cards and building entry',
      notesKey: 'access_cards_deactivated_notes' as const,
    },
    {
      key: 'equipment_collected' as const,
      label: '❌ Equipment Collected',
      description: 'Collect laptop, monitors, peripherals, mobile devices, and accessories',
      notesKey: 'equipment_collected_notes' as const,
    },
    {
      key: 'data_backup_completed' as const,
      label: '❌ Data Backup Completed',
      description: 'Backup any necessary data from employee\'s accounts/devices',
      notesKey: 'data_backup_completed_notes' as const,
    },
  ];

  // Form state
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          // Clean background with subtle warmth
          background: prefersDarkMode ? `
            linear-gradient(135deg, #1E293B 0%, #0F172A 25%, #020617 50%, #0F172A 75%, #1E293B 100%)
          ` : `
            linear-gradient(135deg, #FFFFFF 0%, #FAFAFA 25%, #F5F5F5 50%, #FAFAFA 75%, #FFFFFF 100%)
          `,
          overflow: 'hidden',
          py: 4,
        }}
      >
        {/* Large Grid Background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(rgba(249, 115, 22, 0.06) 1px, transparent 1px),
              linear-gradient(90deg, rgba(249, 115, 22, 0.06) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            zIndex: 0,
          }}
        />

        {/* Gradient Orb - Top Right */}
        <Box
          sx={{
            position: 'absolute',
            top: '-25%',
            right: '-15%',
            width: '700px',
            height: '700px',
            background: 'radial-gradient(circle, rgba(249, 115, 22, 0.12) 0%, rgba(251, 146, 60, 0.04) 35%, transparent 65%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            zIndex: 0,
          }}
        />

        {/* Gradient Orb - Bottom Left */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '-20%',
            left: '-10%',
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, rgba(234, 88, 12, 0.08) 0%, rgba(249, 115, 22, 0.03) 40%, transparent 65%)',
            borderRadius: '50%',
            filter: 'blur(70px)',
            zIndex: 0,
          }}
        />

        {/* Gradient Orb - Center (behind card) */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(249, 115, 22, 0.1) 0%, rgba(251, 146, 60, 0.04) 40%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(50px)',
            zIndex: 0,
          }}
        />

        {/* Gradient Orb - Top Left (subtle) */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '-5%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(251, 146, 60, 0.06) 0%, transparent 60%)',
            borderRadius: '50%',
            filter: 'blur(50px)',
            zIndex: 0,
          }}
        />

        {/* Accent Line - Top */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, transparent 0%, #F97316 50%, transparent 100%)',
            zIndex: 1,
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Logo/Icon */}
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '16px',
              bgcolor: '#F97316', // Intentional brand color
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <LogoutIcon sx={{ fontSize: 32, color: 'primary.contrastText' }} />
          </Box>

          {/* Form Card - Glassmorphic */}
          <Card
            sx={{
              width: '100%',
              maxWidth: 680,
              borderRadius: '20px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
              backgroundColor: prefersDarkMode ? 'rgba(30, 41, 59, 0.75)' : 'rgba(255, 255, 255, 0.75)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: prefersDarkMode ? '1px solid rgba(51, 65, 85, 0.5)' : '1px solid rgba(255, 255, 255, 0.5)',
              overflow: 'visible',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                component="h1"
                variant="h1"
                align="center"
                sx={{
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  mb: 1,
                  color: 'text.primary',
                }}
              >
                IT Offboarding Form
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mb: 4 }}
              >
                Complete all IT revocation tasks for the departing employee
              </Typography>

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    '& .MuiAlert-message': {
                      fontSize: '0.875rem',
                    },
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* Completion Progress */}
              <Box
                sx={{
                  mb: 3,
                  p: 2.5,
                  bgcolor: completionPercentage === 100
                    ? (theme => theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.15)' : '#ECFDF5')
                    : (theme => theme.palette.mode === 'dark' ? 'rgba(249, 115, 22, 0.15)' : '#FFF7ED'),
                  borderLeft: `4px solid ${completionPercentage === 100 ? '#10B981' : '#F97316'}`,
                  borderRadius: '8px',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: completionPercentage === 100 ? '#10B981' : '#F97316',
                      fontSize: '0.875rem',
                    }}
                  >
                    {completionPercentage === 100 ? '✅ All Items Complete' : `⏳ ${completedItems} of ${totalItems} Items Completed`}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: completionPercentage === 100 ? '#10B981' : '#F97316',
                      fontSize: '1.5rem',
                    }}
                  >
                    {completionPercentage}%
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${completionPercentage}%`,
                      height: '100%',
                      bgcolor: completionPercentage === 100 ? '#10B981' : '#F97316',
                      transition: 'width 0.3s ease',
                      borderRadius: '4px',
                    }}
                  />
                </Box>
              </Box>

              <Box component="form" onSubmit={handleSubmit} noValidate>
                {/* Employee Info Section */}
                <Box
                  sx={{
                    mb: 3,
                    p: 2.5,
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB',
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB',
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>
                    Employee Information
                  </Typography>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Full Name
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {[formData.first_name, formData.middle_name, formData.last_name].filter(Boolean).join(' ')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Work Email
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formData.work_email || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Last Working Day
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        📅 {formatDate(formData.last_working_day)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Exit Reason
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formData.exit_reason?.replace('_', ' ') || 'Not specified'}
                      </Typography>
                    </Box>
                  </Box>

                  {formData.notes && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5, display: 'block' }}>
                          HR Notes
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                          {formData.notes}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>

                {/* Warning Alert */}
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(251, 146, 60, 0.15)' : '#FFF7ED',
                    color: 'text.primary',
                    '& .MuiAlert-icon': {
                      color: '#F97316',
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    ⚠️ Important: All 6 items must be completed before submission
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    Completing this form will automatically change the employee's status to TERMINATED/RESIGNED.
                  </Typography>
                </Alert>

                {/* IT Revocation Checklist */}
                <Box
                  sx={{
                    mb: 3,
                    p: 2.5,
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(249, 115, 22, 0.15)' : '#FFF7ED',
                    borderLeft: '4px solid #F97316',
                    borderRadius: '8px',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#F97316',
                      fontSize: '0.875rem',
                      mb: 1.5,
                    }}
                  >
                    IT Revocation Checklist
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme => theme.palette.mode === 'dark' ? '#FB923C' : '#EA580C',
                      fontSize: '0.75rem',
                      mb: 2,
                      display: 'block',
                    }}
                  >
                    Please mark the items you have completed for this employee ({completedItems}/{totalItems})
                  </Typography>

                  {checklistItems.map((item) => (
                    <Box
                      key={item.key}
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#FFFFFF',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: checklistState[item.key]
                          ? '#10B981'
                          : (theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB'),
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checklistState[item.key]}
                            onChange={(e) =>
                              setChecklistState({
                                ...checklistState,
                                [item.key]: e.target.checked,
                              })
                            }
                            disabled={submitting}
                            sx={{
                              color: '#F97316',
                              '&.Mui-checked': {
                                color: '#F97316',
                              },
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}>
                              {checklistState[item.key] ? item.label.replace('❌', '✅') : item.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              {item.description}
                            </Typography>
                          </Box>
                        }
                      />

                      {/* Optional notes field for this item */}
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                        placeholder={`Add notes about ${item.label.replace('❌', '').replace('✅', '').trim().toLowerCase()} (optional)`}
                        value={itemNotes[item.notesKey]}
                        onChange={(e) =>
                          setItemNotes({
                            ...itemNotes,
                            [item.notesKey]: e.target.value,
                          })
                        }
                        disabled={submitting}
                        sx={{
                          mt: 1.5,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                          },
                        }}
                        InputProps={{
                          sx: {
                            fontSize: '0.875rem',
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Box>

                {/* General IT Notes */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 0.75,
                      fontWeight: 500,
                      color: 'text.primary',
                      fontSize: '0.875rem',
                    }}
                  >
                    Additional IT Notes (Optional)
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={itNotes}
                    onChange={(e) => setItNotes(e.target.value)}
                    disabled={submitting}
                    placeholder="Add any additional notes about the offboarding process, equipment condition, or special circumstances..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      },
                    }}
                    InputProps={{
                      sx: {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={submitting || completionPercentage !== 100}
                  sx={{
                    height: '48px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '12px',
                    background: completionPercentage === 100
                      ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                      : 'linear-gradient(135deg, #FB923C 0%, #F97316 100%)',
                    boxShadow: completionPercentage === 100
                      ? '0 2px 8px -2px rgba(16, 185, 129, 0.3)'
                      : '0 2px 8px -2px rgba(249, 115, 22, 0.3)',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      background: completionPercentage === 100
                        ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                        : 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                      boxShadow: completionPercentage === 100
                        ? '0 4px 12px -2px rgba(16, 185, 129, 0.4)'
                        : '0 4px 12px -2px rgba(249, 115, 22, 0.4)',
                    },
                    '&:active': {
                      transform: 'scale(0.98)',
                    },
                    '&.Mui-disabled': {
                      background: 'linear-gradient(135deg, #FDBA74 0%, #FB923C 100%)',
                      color: 'rgba(255, 255, 255, 0.8)',
                    },
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : completionPercentage === 100 ? (
                    '✅ Submit IT Revocation Checklist'
                  ) : (
                    `⏳ Complete All Items to Submit (${completedItems}/${totalItems})`
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Footer */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 4, textAlign: 'center' }}
          >
            HR Management System - IT Offboarding & Access Revocation
          </Typography>
        </Box>
      </Container>
    </Box>
    </ThemeProvider>
  );
};

export default ITOffboardingForm;
